import { inject, injectable } from 'inversify';
import { BehaviorSubject, Subject, Subscription, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
    BulkZipJobView,
    BulkZipProgressEvent,
    BulkZipSubmitResponse,
    BulkZipTerminalEvent,
} from '../types/bulk-zip';
import { WebsocketResponse } from '../types/types';
import { ApiConfig } from './api-config';
import { LightHttp } from './light-http.service';
import { WebSocketService } from './websocket.service';

/**
 * Discriminated union of events `BulkZipService` publishes on
 * `events$`. Components subscribe to surface toasts, navigate
 * to the Downloads Center, or refresh local job state.
 */
export type BulkZipServiceEvent =
    | { kind: 'CREATED'; job: BulkZipJobView }
    | { kind: 'EXISTING'; job: BulkZipJobView }
    | { kind: 'COVERED'; coverage: BulkZipSubmitResponse['coverage'] }
    | { kind: 'PROGRESS'; ev: BulkZipProgressEvent }
    | { kind: 'TERMINAL'; ev: BulkZipTerminalEvent };

/**
 * Backend `ProcessType` wire value for bulk-zip events.
 *
 * Matches the kotlin enum value added in
 * `springboot-shared-library/.../workflows/ProcessType.kt`
 * (`BULK_ZIP_DOWNLOAD("bulk-zip-download")`). The convention
 * across `ProcessType` is lowercase-plus-dashes, so this is
 * the value that arrives on the wire.
 */
const BULK_ZIP_PROCESS_TYPE = 'bulk-zip-download';

/**
 * Frontend service for the Bulk ZIP download feature.
 *
 * Responsibilities:
 *  - Submit bulk-zip jobs (`submit`).
 *  - List the user's bulk-zip jobs (`list`).
 *  - Delete a bulk-zip job (`delete`).
 *  - Subscribe to WebSocket progress/terminal events for the
 *    user's active jobs and project them onto typed
 *    `BulkZipServiceEvent`s.
 *  - Maintain a cached `BehaviorSubject<BulkZipJobView[]>` so
 *    components (Downloads Center, completion banner) can
 *    render the latest state without re-fetching.
 *
 * The WebSocket layer (`WebSocketService.getWebsocketMessageObserver`)
 * delivers a multiplexed stream — one observable for every
 * notification the user receives. We filter on
 * `processType === BULK_ZIP_PROCESS_TYPE` AND `uuid` being
 * one of our cached jobIds, so unrelated convert-one-to-one
 * messages don't churn `jobs$`.
 */
@injectable()
export class BulkZipService {
    public readonly events$ = new Subject<BulkZipServiceEvent>();
    public readonly jobs$ = new BehaviorSubject<BulkZipJobView[]>([]);

    /**
     * Fires when something external (e.g. the header pill) asks
     * the Downloads Center to open. The center subscribes to
     * this and flips `open: true`. Distinct from `events$` so
     * that "open without context" doesn't have to be modeled as
     * a fake CREATED/EXISTING/COVERED event.
     */
    public readonly openRequested$ = new Subject<void>();

    private wsSubscription?: Subscription;

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(WebSocketService) private ws: WebSocketService,
        @inject(ApiConfig) private apiConfig: ApiConfig,
    ) {
        this.wsSubscription = this.ws.getWebsocketMessageObserver()
            .pipe(filter((msg) => this.isBulkZipMessage(msg)))
            .subscribe((msg) => this.handleWsMessage(msg));
    }

    /**
     * Submit a bulk-zip request for the supplied fileIds. The
     * server may respond with one of three statuses:
     *  - JOB_CREATED: a brand-new job was queued.
     *  - JOB_EXISTING: an active job already covers the same
     *    fileIds (de-duped server-side; we surface the existing
     *    job view).
     *  - ALL_FILES_COVERED: every supplied fileId is already
     *    bundled in a recent completed job; no new job needed.
     *
     * fileIds are sorted client-side before POSTing so the
     * server's de-dup signature lookup is deterministic
     * regardless of UI ordering.
     */
    async submit(rawFileIds: string[]): Promise<BulkZipSubmitResponse> {
        const fileIds = [...rawFileIds].sort();
        const resp = await firstValueFrom(
            this.http.post<BulkZipSubmitResponse>(
                this.apiConfig.getBulkZipBaseEndpoint(),
                { fileIds },
            ),
        );
        if (resp.status === 'ALL_FILES_COVERED') {
            this.events$.next({ kind: 'COVERED', coverage: resp.coverage });
        } else if (resp.job) {
            this.upsertJob(resp.job);
            this.events$.next({
                kind: resp.status === 'JOB_CREATED' ? 'CREATED' : 'EXISTING',
                job: resp.job,
            });
        }
        return resp;
    }

    /** List the current user's bulk-zip jobs. */
    async list(): Promise<BulkZipJobView[]> {
        const jobs = await firstValueFrom(
            this.http.get<BulkZipJobView[]>(this.apiConfig.getBulkZipBaseEndpoint()),
        );
        this.jobs$.next(jobs ?? []);
        return jobs ?? [];
    }

    /** Delete a bulk-zip job (and its artifact) by jobId. */
    async delete(jobId: string): Promise<void> {
        await firstValueFrom(
            this.http.delete<void>(`${this.apiConfig.getBulkZipBaseEndpoint()}/${jobId}`),
        );
        this.jobs$.next(this.jobs$.value.filter((j) => j.jobId !== jobId));
    }

    /**
     * Build the public download URL for a completed job. Does
     * NOT trigger a network call — the caller embeds this URL
     * in an `<a download>` or fetch call.
     */
    downloadUrl(jobId: string): string {
        return `${this.apiConfig.getBulkZipBaseEndpoint()}/${jobId}/download`;
    }

    /** Current snapshot of cached jobs (synchronous). */
    getCachedJobs(): BulkZipJobView[] {
        return this.jobs$.value;
    }

    /**
     * Refresh the cached job list from the server and ask the
     * Downloads Center to surface itself. Used by the header
     * pill and any "show my bulk downloads" entry points.
     */
    openCenter(): void {
        // Fire-and-forget — center renders eagerly from the
        // current cache and updates when `list()` resolves.
        void this.list();
        this.openRequested$.next();
    }

    /**
     * Tear down the long-lived WS subscription. Mostly for
     * tests / hot-reload — singletons live for the page
     * lifetime in production.
     */
    public dispose(): void {
        if (this.wsSubscription) {
            this.wsSubscription.unsubscribe();
            this.wsSubscription = undefined;
        }
    }

    private isBulkZipMessage(msg: WebsocketResponse): boolean {
        // Compare against the literal string; cast to unknown
        // so TS doesn't complain about WebsocketProcessType not
        // including the bulk-zip variant yet.
        if ((msg.processType as unknown as string) !== BULK_ZIP_PROCESS_TYPE) {
            return false;
        }
        return this.jobs$.value.some((j) => j.jobId === msg.uuid);
    }

    private handleWsMessage(msg: WebsocketResponse): void {
        const payload = msg.data ? safeJsonParse(msg.data) : {};
        const stage = String(msg.processStage);

        if (stage === 'BUILDING') {
            const ev: BulkZipProgressEvent = {
                type: 'BULK_ZIP_PROGRESS',
                jobId: msg.uuid,
                bytesFlushed: payload.bytesFlushed ?? 0,
                totalBytes: payload.totalBytes ?? 0,
                filesCompleted: payload.filesCompleted ?? 0,
                totalFiles: payload.totalFiles ?? 0,
                currentFile: payload.currentFile ?? null,
                ts: payload.ts ?? new Date().toISOString(),
            };
            this.applyProgress(ev);
            this.events$.next({ kind: 'PROGRESS', ev });
        } else if (stage === 'COMPLETED') {
            const failed = String(msg.processStageSubState).toUpperCase() === 'FAILED';
            const ev: BulkZipTerminalEvent = {
                type: failed ? 'BULK_ZIP_FAILED' : 'BULK_ZIP_COMPLETED',
                jobId: msg.uuid,
                errorCode: payload.errorCode,
                ts: payload.ts ?? new Date().toISOString(),
            };
            this.applyTerminal(ev);
            this.events$.next({ kind: 'TERMINAL', ev });
        }
        // BULK_ZIP_DELETED handling deferred — backend defines
        // the wire path for delete-broadcasts (e.g. multi-tab
        // delete sync) once phase 3 lands.
    }

    private upsertJob(job: BulkZipJobView): void {
        const next = this.jobs$.value.filter((j) => j.jobId !== job.jobId);
        next.unshift(job);
        this.jobs$.next(next);
    }

    private applyProgress(ev: BulkZipProgressEvent): void {
        this.jobs$.next(
            this.jobs$.value.map((j) =>
                j.jobId === ev.jobId
                    ? {
                          ...j,
                          bytesFlushed: ev.bytesFlushed,
                          totalBytes: ev.totalBytes || j.totalBytes,
                          filesCompleted: ev.filesCompleted,
                          totalFiles: ev.totalFiles || j.totalFiles,
                          currentFile: ev.currentFile,
                          processStage: 'BUILDING',
                          processStageSubState: 'IN_PROGRESS',
                      }
                    : j,
            ),
        );
    }

    private applyTerminal(ev: BulkZipTerminalEvent): void {
        this.jobs$.next(
            this.jobs$.value.map((j) =>
                j.jobId === ev.jobId
                    ? {
                          ...j,
                          processStage: 'COMPLETED',
                          processStageSubState: ev.type === 'BULK_ZIP_FAILED' ? 'FAILED' : 'COMPLETED',
                          failed: ev.type === 'BULK_ZIP_FAILED',
                      }
                    : j,
            ),
        );
    }
}

function safeJsonParse(s: string): any {
    try {
        return JSON.parse(s);
    } catch {
        return {};
    }
}
